import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { ForwardedRef, Fragment, forwardRef, useCallback } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';

import { useSidebarMenuContext } from '~/components/Sidebar/SidebarMenu/context';
import { NavigationLevelPathConfig } from '~/components/Sidebar/SidebarMenu/SidebarMenuLevel/types';
import { useSxStyles } from '~/components/Sidebar/SidebarMenu/SidebarMenuLevel/useStyles';

type PropsType = {
  parentLevels: string[];
  activeLevels: string[];
  levelPaths: NavigationLevelPathConfig[];
};

const CustomNavLink = forwardRef(
  (props: NavLinkProps, ref: ForwardedRef<HTMLAnchorElement>) => (
    <NavLink ref={ref} {...props} activeClassName={'Mui-selected'} />
  ),
);
CustomNavLink.displayName = 'CustomNavLink';

export const SidebarMenuLevel = (props: PropsType) => {
  const { parentLevels, levelPaths, activeLevels } = props;

  const sxStyles = useSxStyles();

  const level = parentLevels.length;

  const { navigationRootPath, pathname, openMenuItems, toggleOpenMenuItems } =
    useSidebarMenuContext();

  const getFullPath = useCallback(
    (path?: string): string => {
      const parentPath =
        parentLevels.length > 0
          ? `${navigationRootPath}${parentLevels.join('/')}/`
          : navigationRootPath;

      return !!path ? `${parentPath}/${path}` : parentPath;
    },
    [navigationRootPath, parentLevels],
  );

  const isActivePath = useCallback(
    (pathTo: string): boolean => {
      // Add slash at end to ensure comparison of exact path segments.
      return pathname ? `${pathname}/`.startsWith(`${pathTo}/`) : false;
    },
    [pathname],
  );

  const filterDisabled = useCallback((path: NavigationLevelPathConfig) => {
    return !(path.hasOwnProperty('isAvailable') && !path.isAvailable);
  }, []);

  const getPathTo = useCallback(
    (path: string) => {
      const parentPath =
        parentLevels.length > 0 ? `${parentLevels.join('/')}/` : '';

      return `${navigationRootPath}/${parentPath}${path}`;
    },
    [navigationRootPath, parentLevels],
  );

  const getItemKey = useCallback((level: number, index: number) => {
    return `nav-item-${level}-${index}`;
  }, []);

  const isOpen = useCallback(
    (path: string) => {
      return openMenuItems.includes(getFullPath(path));
    },
    [openMenuItems, getFullPath],
  );

  const handleOpen = useCallback(
    (path: string) => {
      toggleOpenMenuItems(getFullPath(path));
    },
    [toggleOpenMenuItems, getFullPath],
  );

  return (
    <List sx={sxStyles.getLevelSx(level)}>
      {levelPaths.filter(filterDisabled).map((item, index) => {
        const pathTo = getPathTo(item.path);
        const key = getItemKey(level, index);

        if (!!item.subPaths && item.subPaths.length > 0) {
          const isItemOpen = isOpen(item.path);

          return (
            <Fragment key={key}>
              <ListItem sx={sxStyles.listItem}>
                <ListItemButton
                  selected={isActivePath(pathTo)}
                  onClick={() => handleOpen(item.path)}
                >
                  <ListItemText>{item.label}</ListItemText>
                </ListItemButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleOpen(item.path)}
                  sx={sxStyles.expandButton}
                >
                  {isItemOpen ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </ListItem>
              <Collapse in={isItemOpen}>
                <SidebarMenuLevel
                  parentLevels={[...parentLevels, item.path]}
                  levelPaths={item.subPaths}
                  activeLevels={activeLevels}
                />
              </Collapse>
            </Fragment>
          );
        }

        return (
          <ListItemButton component={CustomNavLink} to={pathTo} key={key}>
            <ListItemText>{item.label}</ListItemText>
          </ListItemButton>
        );
      })}
    </List>
  );
};
